import { useMutation, useQuery } from '@apollo/client/react';
import {
  CREATE_DEMO_COUPON,
  DEMO_COUPONS,
  DEMO_COUPON_QUOTE,
  STAFF_BILLING_HARDWARE_CATALOG,
  SYNC_DEMO_COUPON_USAGE,
} from '@staff-app/graphql-client';
import type {
  CreateDemoCouponInput,
  DemoCoupon,
  DemoCouponPage,
  DemoCouponQuote,
  DemoCouponStatus,
} from '@staff-app/shared-types';
import { useDebounced } from './useOrderDeviceTasks';

/** Rows per page. Demo coupons accrue slowly — one page holds years of them. */
export const DEMO_PAGE_SIZE = 200;

export function useDemoCoupons(search: string, status: string, offset = 0) {
  const debouncedSearch = useDebounced(search.trim(), 300);
  return useQuery<{ demoCoupons: DemoCouponPage }>(DEMO_COUPONS, {
    variables: {
      filter: {
        search: debouncedSearch || undefined,
        status: (status || undefined) as DemoCouponStatus | undefined,
      },
      pagination: { offset, limit: DEMO_PAGE_SIZE },
    },
    fetchPolicy: 'cache-and-network',
  });
}

/**
 * The catalog-priced value of the coupon being composed. Skipped until a device and quantity
 * are chosen, so typing in the reason box does not fire a Mongo price read.
 */
export function useDemoCouponQuote(sku: string, quantity: number) {
  return useQuery<{ demoCouponQuote: DemoCouponQuote }>(DEMO_COUPON_QUOTE, {
    variables: { sku, quantity },
    skip: !sku || !(quantity > 0),
    fetchPolicy: 'cache-and-network',
  });
}

export function useCreateDemoCoupon() {
  return useMutation<{ createDemoCoupon: DemoCoupon }, { input: CreateDemoCouponInput }>(
    CREATE_DEMO_COUPON,
    // The new row belongs at the top of the list the operator is looking at.
    { refetchQueries: ['demoCoupons'] },
  );
}

export function useSyncDemoCouponUsage() {
  return useMutation<{ syncDemoCouponUsage: number }, { codes?: string[] }>(SYNC_DEMO_COUPON_USAGE);
}

/** One selectable device: a catalog product, not one of its Stripe price mappings. */
export interface DemoDeviceOption {
  sku: string;
  name: string;
  usOnly: boolean;
  isKiosk: boolean;
}

/**
 * Devices the demo card can offer.
 *
 * Reuses the Billing Console's catalog query — a catalog edit shows up with no deploy — with two
 * adjustments:
 *
 *  - **Deduped by SKU.** `staffBillingHardwareCatalog` returns one OPTION per Stripe price
 *    mapping, so a product with two configurations appears twice. A demo coupon is priced from
 *    the Mongo catalog, not from a Stripe price, so the second row would be the same choice
 *    twice over.
 *  - **`invoiceable` is ignored** for the same reason: it means "has a usable Stripe price",
 *    which no part of minting a coupon depends on.
 *  - **Kiosks are excluded.** Demo coupons are players-only (requester, 2026-08-24) — the
 *    Android Stick, Pro and ProMax. Filtered on `isKiosk`, the catalog's own word, NOT on a SKU
 *    list (a kiosk added tomorrow is excluded with no edit here) and NOT on `usOnly`, which is
 *    equal today but answers a different question — where a product ships, rather than what it
 *    is. The server refuses a kiosk too; this only keeps the picker honest about it.
 *  - **Nothing is filtered by `usOnly`.** Demo devices ship US only, so it can never exclude
 *    anything here. Kept on the option shape because it is what will decide the day
 *    international demos are allowed.
 */
export function useDemoDeviceOptions() {
  const { data, loading, error } = useQuery<{
    staffBillingHardwareCatalog: {
      products: { sku: string; name: string; usOnly: boolean; isKiosk: boolean }[];
    };
  }>(STAFF_BILLING_HARDWARE_CATALOG, { fetchPolicy: 'cache-first' });

  const bySku = new Map<string, DemoDeviceOption>();
  for (const p of data?.staffBillingHardwareCatalog?.products ?? []) {
    if (p.isKiosk) continue; // players only
    if (!bySku.has(p.sku)) {
      bySku.set(p.sku, { sku: p.sku, name: p.name, usOnly: p.usOnly, isKiosk: p.isKiosk });
    }
  }
  const options = [...bySku.values()].sort((a, b) => a.name.localeCompare(b.name));

  return { options, loading, error };
}
